/**
 * injectInactivityTimeout - Inyecta configuración de timeout de inactividad para el chat
 *
 * Este módulo permite ajustar el tiempo de inactividad antes de ejecutar el evento "no-activity"
 * mediante la variable CSS --chat-no-activity-timeout
 *
 * @module injectInactivityTimeout
 */

/**
 * Inyecta la configuración de timeout de inactividad en el DOM
 *
 * Lee la configuración de no-activity-seconds desde chat-colors y aplica la constante
 * correspondiente como CSS variable para que el hook useInactivityTimer pueda usarla
 *
 * @example
 * // En el loader, después de cargar la configuración:
 * injectInactivityTimeout();
 *
 * @returns {void}
 */
export function injectInactivityTimeout(): void {
  try {
    // Obtener la configuración de no-activity-seconds desde chat-colors
    const config              = ( window as any ).HELLOMATIK_CONFIG || {};
    const chatColors          = config['chat-colors'] || {};
    const noActivitySeconds   = chatColors['no-activity-seconds'] ?? config['no-activity-seconds'];

    // Usar 8 segundos por defecto si no está configurado o es inválido
    // CRÍTICO: Permitir 0 segundos (necesario para configurar 0 minutos + X segundos)
    const parsedTimeout = typeof noActivitySeconds === 'number'
      ? noActivitySeconds
      : Number.parseInt( String( noActivitySeconds ?? '' ), 10 );
    const timeoutSeconds = Number.isFinite( parsedTimeout ) && parsedTimeout >= 0
      ? parsedTimeout
      : 8;

    // Crear elemento <style> para la variable CSS
    const styleElement = document.createElement( 'style' );
    styleElement.id = 'hellomatik-inactivity-timeout';

    // Construir el CSS con la variable aplicada solo a #hm-popup
    let cssRules = '/* Hellomatik - Inactivity timeout for #hm-popup */\n#hm-popup {\n';
    cssRules += `  --chat-no-activity-timeout: ${timeoutSeconds};\n`;
    cssRules += '}\n';

    styleElement.textContent = cssRules;

    // Insertar en el <head> del documento
    document.head.appendChild( styleElement );

  } catch( error ) {
    if( import.meta.env.DEV ) console.error( '[Inactivity Timeout] Failed to inject timeout:', error );
  }
}

/**
 * Resetea la configuración de timeout de inactividad a los valores por defecto
 * Útil para pruebas o cuando se quiere volver a la configuración original
 *
 * @returns {void}
 */
export function resetInactivityTimeout(): void {
  const styleElement = document.getElementById( 'hellomatik-inactivity-timeout' );

  if( styleElement )
    styleElement.remove();
}
